import {
  Button,
  ChatSearchIcon,
  ConversationCandidateResponse,
  ConversationListItemResponse,
  ConversationSupportAudience,
  createConversationGroup,
  Disclaimer,
  Div,
  H5,
  lightTheme,
  ListSpinner,
  ModalPortal,
  openDirectConversation,
  openSupportConversation,
  queryKeys,
  Radio,
  renderRole,
  reportError,
  SearchBar,
  SearchField,
  SimpleLocationEntity,
  SuccessToast,
  useAuthenticatedAuthUser,
  useDebouncedValue,
} from '@vgrubs/components';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CandidateLocationFilter } from './components/CandidateLocationFilter';
import { ConversationPersonRow } from './components/ConversationPersonRow';
import { GroupIdentityFields } from './components/GroupIdentityFields';
import { CONVERSATION_MODAL_HEIGHT } from './conversationModal';
import {
  CandidatePool,
  useConversationCandidates,
} from './hooks/useConversationQueries';
import {
  CandidateList,
  ModeTab,
  OpenedChip,
} from './NewConversationModal.styled';
import { ChatScope } from './hooks/useChatFilters';
import { uploadGroupImage } from './uploadGroupImage';
import { openUserDetails } from './userDetailsPath';

/**
 * Start a conversation.
 *
 * What it offers follows the tab the inbox is on, rather than asking again:
 *
 *   - **Merchants** - a support thread with a merchant (needs
 *     `conversationWrite`), or a merchant group: restaurants, sales agents and
 *     colleagues in one room. Making a group is the admin and support roles'
 *     job, so the second tab is theirs alone, and is the only one left for
 *     either of them without the permission.
 *   - **Sales** - a support thread with a sales agent. One kind of thread, so
 *     no tabs at all here: just the people.
 *   - **Internal Team** - a 1:1 with someone internal, or a group of them. Two
 *     tabs, User and Group; creating a group is reserved for the admin role, so
 *     that one is absent for everyone else rather than failing on save.
 *
 * The question "who am I talking to" is already answered by where you were
 * standing when you pressed "+", and asking it twice is how a three-tab modal
 * ends up opening a support thread from the internal team's list.
 *
 * Support and direct are idempotent on the backend: opening a thread that
 * already exists returns it. That is why a candidate who already has one is
 * still clickable - `hasChat` is shown as context, not as a block.
 */

const SEARCH_DEBOUNCE_MS = 400;

type Mode = 'support' | 'direct' | 'group' | 'merchantGroup';

/**
 * Who each mode can be with: a direct message is between colleagues, an
 * internal group takes sales agents beside them, a merchant group takes
 * restaurants too, and a support thread is with someone outside the company.
 */
const POOL_BY_MODE: Record<Mode, CandidatePool> = {
  direct: 'internal',
  group: 'group',
  merchantGroup: 'merchantGroup',
  support: 'support',
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Which half of the inbox the "+" was pressed in. */
  scope: ChatScope;
  /** `conversationWrite` - opening a support thread is delegated work. */
  canOpenSupport: boolean;
  /**
   * Admin and support roles - the backend opened create, rename and
   * membership to support. Marketing stays a participant.
   */
  canCreateGroups: boolean;
  /** Called with the conversation to select once it exists. */
  onOpened: (conversationId: number) => void;
};

/** What the modal offers on a tab, in order - the first is where it opens. */
const modesFor = (
  scope: ChatScope,
  canOpenSupport: boolean,
  canCreateGroups: boolean
): Array<{ value: Mode; label: string }> => {
  if (scope === 'internal') {
    return [
      { value: 'direct', label: 'Users' },
      ...(canCreateGroups
        ? [{ value: 'group' as Mode, label: 'Group Chat' }]
        : []),
    ];
  }
  if (scope === 'sales') return [{ value: 'support', label: 'Sales' }];
  return [
    ...(canOpenSupport
      ? [{ value: 'support' as Mode, label: 'Merchant' }]
      : []),
    ...(canCreateGroups
      ? [{ value: 'merchantGroup' as Mode, label: 'Group Chat' }]
      : []),
  ];
};

export const NewConversationModal = ({
  isOpen,
  onClose,
  scope,
  canOpenSupport,
  canCreateGroups,
  onOpened,
}: Props) => {
  const queryClient = useQueryClient();
  const user = useAuthenticatedAuthUser();

  const modes = modesFor(scope, canOpenSupport, canCreateGroups);
  const firstMode = modes[0]?.value ?? 'support';

  const [mode, setMode] = useState<Mode>(firstMode);
  /** Both kinds of group share the draft below; only the pool differs. */
  const groupMode = mode === 'group' || mode === 'merchantGroup';
  /* The merchant group picker's "everyone at this restaurant". */
  const [location, setLocation] = useState<SimpleLocationEntity | null>(null);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const spinnerRef = useRef<HTMLDivElement | null>(null);

  /* Group draft */
  const [title, setTitle] = useState('');
  const [image, setImage] = useState<File | Blob | string | null>(null);
  const [selected, setSelected] = useState<ConversationCandidateResponse[]>([]);

  const debounced = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);

  /* Reopening should not resume somebody else's half-finished group. */
  useEffect(() => {
    if (isOpen) return;
    setQuery('');
    setTitle('');
    setImage(null);
    setSelected([]);
    setLocation(null);
    setMode(firstMode);
  }, [isOpen, scope, firstMode]);

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useConversationCandidates({
      query: debounced,
      pool: POOL_BY_MODE[mode],
      locationId: mode === 'merchantGroup' ? location?.id ?? null : null,
      /* On a support tab, only that tab's outside users: the merchant queue
       must not offer a sales agent, or the thread it makes lands in the other
       tab and looks lost. */
      supportAudience:
        mode === 'support' && scope !== 'internal'
          ? (scope as ConversationSupportAudience)
          : undefined,
      enabled: isOpen,
    });

  const candidates = useMemo(
    () =>
      // You are always in your own groups, and a direct message with yourself
      // is not a thing the backend will make.
      (data ?? []).filter((candidate) => candidate.userId !== user.id),
    [data, user.id]
  );

  /*
   * The next page, when the sentinel under the list comes into view.
   *
   * Rooted on the viewport rather than on a scroll container: the list scrolls
   * inside the modal, which is itself on screen, so the sentinel becomes
   * visible exactly when somebody reaches the bottom of it. The margin asks for
   * the next page a little before that, so the list does not stutter.
   */
  useEffect(() => {
    const node = spinnerRef.current;
    if (!node || !hasNextPage || isFetchingNextPage) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      },
      { rootMargin: '0px 0px 200px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, candidates.length]);

  const refreshInbox = () =>
    queryClient.resetQueries({ queryKey: queryKeys.conversations.inboxAll });

  /**
   * Both are idempotent, so a double click cannot make a second thread.
   *
   * The tab decides which one, and it can again: the picker is now filtered by
   * audience, so a support tab only ever offers people a support thread is
   * made with - sales agents among them, which is what the backend went back
   * to after briefly treating them as colleagues.
   */
  const openWith = async (candidate: ConversationCandidateResponse) => {
    if (busy) return;
    try {
      setBusy(true);
      const { data: response } = await (mode === 'support'
        ? openSupportConversation(candidate.userId)
        : openDirectConversation(candidate.userId));
      const conversation = response.data as ConversationListItemResponse;
      refreshInbox();
      onOpened(conversation.id);
      onClose();
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const toggleSelected = (candidate: ConversationCandidateResponse) =>
    setSelected((current) =>
      current.some((item) => item.userId === candidate.userId)
        ? current.filter((item) => item.userId !== candidate.userId)
        : [...current, candidate]
    );

  const createGroup = async () => {
    if (busy || !title.trim() || selected.length === 0) return;
    try {
      setBusy(true);
      const imageKey = await uploadGroupImage(image);
      const { data: response } = await createConversationGroup({
        title: title.trim(),
        ...(imageKey ? { imageKey } : {}),
        /* Left off for an internal group - that is the default, and an API
           from before merchant groups would not know the field. */
        ...(mode === 'merchantGroup' ? { type: 'merchantGroup' as const } : {}),
        userIds: selected.map((candidate) => candidate.userId),
      });
      SuccessToast('Group created.');
      refreshInbox();
      onOpened(response.data.id);
      onClose();
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const isSelected = (candidate: ConversationCandidateResponse) =>
    selected.some((item) => item.userId === candidate.userId);

  return (
    <ModalPortal
      isOpen={isOpen}
      closeModal={onClose}
      header="New Conversation"
      width="520px"
      minHeight={CONVERSATION_MODAL_HEIGHT}
      maxHeight={CONVERSATION_MODAL_HEIGHT}
      footer={
        groupMode ? (
          <Button
            $w="100%"
            $wSM="100%"
            disabled={busy || !title.trim() || selected.length === 0}
            onClick={createGroup}
            $selected
          >
            {selected.length > 0
              ? `Create Group (${selected.length})`
              : 'Create Group'}
          </Button>
        ) : undefined
      }
      $numOfFloatingButtons={groupMode ? 1 : undefined}
    >
      {modes.length > 1 && (
        <Div $flex $gap="8px" $mb="16">
          {modes.map((item) => (
            <ModeTab
              key={item.value}
              $small
              $minWidthAuto
              $minWidthAutoMD
              $minWidthAutoSM
              selected={mode === item.value}
              onClick={() => {
                setMode(item.value);
                setSelected([]);
                setLocation(null);
              }}
            >
              {item.label}
            </ModeTab>
          ))}
        </Div>
      )}

      {groupMode && (
        <Div $mb="16">
          <GroupIdentityFields
            title={title}
            onTitleChange={setTitle}
            placeholder={
              mode === 'merchantGroup'
                ? 'e.g. Burgerito - ops'
                : 'e.g. Ops team'
            }
            image={image}
            onImageChange={setImage}
            onImageDelete={() => setImage(null)}
          />

          <H5 $mb="4">Members</H5>
          <Disclaimer $m="0" $mb="8" $color={lightTheme.lightGray}>
            {mode === 'merchantGroup'
              ? 'Restaurant owners and staff, sales agents and the vGrubs team. Everyone sees everyone else by name. You are added automatically as the owner.'
              : 'The vGrubs team and sales agents. You are added automatically as the owner.'}
          </Disclaimer>
        </Div>
      )}

      {/* The same field as the composer and the inbox search, so the three
          read as one control repeated rather than three lookalikes. */}
      <SearchBar>
        <SearchField
          value={query}
          placeholder="Search"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setQuery(event.target.value)
          }
        />
        <ChatSearchIcon />
      </SearchBar>

      {mode === 'merchantGroup' && (
        <CandidateLocationFilter location={location} onChange={setLocation} />
      )}

      <CandidateList>
        {candidates.map((candidate) => (
          <ConversationPersonRow
            key={candidate.userId}
            name={candidate.name}
            /* Same separator the agent list uses: a hyphen between a role and
               a phone number reads as part of the number. */
            subtitle={[renderRole(candidate.role), candidate.phoneNumber]
              .filter(Boolean)
              .join(' · ')}
            /* Under it rather than in it: with a few locations the line ran
               out of room and took the phone number with it. */
            detail={candidate.locations}
            selected={groupMode && isSelected(candidate)}
            onClick={() =>
              groupMode ? toggleSelected(candidate) : openWith(candidate)
            }
            /* Not in group mode: there the whole row is a checkbox, and a link
               inside a checkbox opens a tab when you meant to tick someone. */
            onNameClick={
              groupMode
                ? undefined
                : () => openUserDetails(candidate.userId, candidate.role)
            }
            trailing={
              groupMode ? (
                <Radio checked={isSelected(candidate)} />
              ) : (
                candidate.hasChat && <OpenedChip>Opened</OpenedChip>
              )
            }
          />
        ))}

        {/* Always rendered, collapsed to 1px while idle: it is the paging
            sentinel as much as it is the spinner, and an element that only
            exists while loading can never be the thing that triggers it. */}
        <ListSpinner
          loading={isLoading || isFetchingNextPage}
          observerRef={spinnerRef}
          height={isLoading || isFetchingNextPage ? '70px' : '1px'}
          heightSM={isLoading || isFetchingNextPage ? '70px' : '1px'}
          /* Its own default is -40, which pulls the parent's box up PAST this
             element: the sentinel then sits outside the scroll area, clipped,
             and never intersects anything - the paging simply never fires. */
          mb="0"
        />

        {!isLoading && candidates.length === 0 && (
          <Div $flex $center $p="24px">
            <Disclaimer $m="0" $center $color={lightTheme.lightGray}>
              Nobody matches that search.
            </Disclaimer>
          </Div>
        )}
      </CandidateList>
    </ModalPortal>
  );
};
